import type { GetServerSideProps } from 'next';
import { LandingLayout } from 'apps/players/layouts/landing';
import { ProfileLayout } from 'apps/players/layouts/profile';
import {
    getPlayerProfie,
    GetPlayerProfileResponse,
    getPlayers,
    GetPlayersResponse,
} from 'services/players';
import { GetFooterResponse } from 'services/mancity-template/get/types/get-footer.type';
import { GetMainNavResponse } from 'services/mancity-template/get/types/get-main-nav.type';
import { GetPartnerLogosResponse } from 'services/mancity-template/get/types/get-partner-logos.type';
import { getFooter, getMainNav, getPartnerLogos } from 'services/mancity-template';
import { Seo } from 'core/mancity/components/general/seo';
import { initI18n, getAppI18nConfig } from 'cfgi18n';

const landingQuery = ['mens', 'womens', 'eds', 'academy', 'esports'];

type PageProps = {
    playersListData: GetPlayersResponse | null;
    playerProfileData: GetPlayerProfileResponse | null;
    team: string;
    footerData?: GetFooterResponse;
    mainNavData?: GetMainNavResponse;
    partnersData?: GetPartnerLogosResponse;
};

export default function Page({ playersListData, playerProfileData, team }: PageProps) {
    return (
        <>
            {(playersListData?.head || playerProfileData?.head) && (
                <Seo head={playersListData ? playersListData.head : playerProfileData!.head} />
            )}
            {playersListData && <LandingLayout team={team} data={playersListData} />}
            {playerProfileData && <ProfileLayout data={playerProfileData} />}
        </>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const { query } = context.params as { query: Array<string> };
    const { fromapp } = context.query;
    const isFromApp = typeof fromapp !== 'undefined';
    const isLanding = Array.isArray(query) && landingQuery.includes(query[0]);
    const locale = context.locale as string;

    try {
        let playersListData = null;
        let playerProfileData = null;
        let footerData: GetFooterResponse | undefined;
        let mainNavData: GetMainNavResponse | undefined;
        let partnersData: GetPartnerLogosResponse | undefined;

        const { supportedLocales } = await getAppI18nConfig('players');
        await initI18n(locale, supportedLocales);

        if (!isFromApp) {
            footerData = await getFooter(locale).then((response) => response.data);
            mainNavData = await getMainNav(locale).then((response) => response.data);
            partnersData = await getPartnerLogos(locale).then((response) => response.data);
        }

        if (isLanding) {
            const response = await getPlayers(query[0], locale);
            playersListData = response.data;
        } else {
            const response = await getPlayerProfie(query[0], locale);
            playerProfileData = response.data;
        }

        if (!playersListData && !playerProfileData) {
            return { notFound: true };
        }

        return {
            props: {
                playersListData,
                playerProfileData,
                team: query[0],
                footerData,
                mainNavData,
                partnersData,
                locale,
            },
        };
    } catch (error) {
        console.error('Server-side error:', error);
        return {
            redirect: {
                destination: '/500',
                permanent: false,
            },
        };
    }
};
